import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { Pill } from "@/components/ui/Pill";
import { DEFAULT_LANG } from "@/i18n";

interface Props {
  /** Key under ``quote:adminInsight.<key>`` — reads ``what`` and ``cta``. */
  insightKey: string;
  /** Admin route without the language prefix (``/admin/machines``). */
  href: string;
  lang?: string;
  testId: string;
}

/**
 * KAN-1923 — the owner's half of a customer-facing dead end, in the same
 * „Admin insight" style as the price card's diagnosis (KAN-988).
 *
 * The caller decides WHETHER it renders (signed-in admin of THIS shop only);
 * this component only says what to fix and links to the screen that fixes it.
 * The customer sentence next to it never names an admin screen.
 */
export function AdminInsightNote({
  insightKey,
  href,
  lang,
  testId,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  return (
    <div
      className="mt-3 rounded-md border border-border bg-surface-2 p-3 text-left"
      data-testid={testId}
    >
      <Pill tone="accent">{t("adminInsight.pill")}</Pill>
      <p className="mt-2 text-sm text-ink">
        {t(`adminInsight.${insightKey}.what`)}
      </p>
      <a
        className="mt-2 inline-flex text-[13px] font-medium text-accent-ink underline"
        data-testid={`${testId}-fix`}
        href={`/${lang || DEFAULT_LANG}${href}`}
      >
        {t(`adminInsight.${insightKey}.cta`)}
      </a>
    </div>
  );
}
